import area from "@turf/area";
import booleanContains from "@turf/boolean-contains";
import booleanOverlap from "@turf/boolean-overlap";
import { featureCollection, polygon } from "@turf/helpers";
import intersect from "@turf/intersect";
import { memorize } from "./../utils/optimization";
import { convertPointTo3sf } from "./coordinates";

type Coordinate = [number, number, number?, number?]; // [x, y, z?, m?]

export interface IntersectionResult {
  intersections: {
    type: "overlap" | "collision";
    coordinates: number[][]; // The 2D coordinates of the intersection polygon
  }[];
}

/**
 * Finds the intersections between two polygons and classifies them as 'overlap' or 'collision'.
 *
 * @param polygon1 - The first polygon as an array of coordinates [x, y, z, m].
 * @param polygon2 - The second polygon as an array of coordinates [x, y, z, m].
 * @param buffer - The vertical buffer distance to add to the height ranges.
 * @returns An object containing the intersections categorized as 'overlap' or 'collision'.
 */
export function findPolygonIntersections(
  polygon1: Coordinate[],
  polygon2: Coordinate[],
  buffer: number = 0 // Vertical buffer distance
): IntersectionResult {
  // Convert polygons to Turf.js GeoJSON Polygons (ignore Z and M for 2D checks)
  const turfPolygon1 = polygon([polygon1.map(([x, y]) => [x, y])]);
  const turfPolygon2 = polygon([polygon2.map(([x, y]) => [x, y])]);

  // Use Turf.js to find all intersections (can return multiple features)
  const intersection = intersect(
    featureCollection([turfPolygon1, turfPolygon2])
  );

  if (!intersection || !intersection.geometry) {
    // No intersection in 2D
    return { intersections: [] };
  }

  // Extract the intersection geometries (handles MultiPolygon or Polygon)
  const geometries =
    intersection.geometry.type === "MultiPolygon"
      ? intersection.geometry.coordinates
      : [intersection.geometry.coordinates];

  // Iterate through each intersecting geometry and determine type
  const intersections = geometries.map((geometry) => {
    const intersectionCoordinates: number[][] = geometry[0]; // Get the first ring of the polygon

    // Get height ranges for this intersecting area
    const [z1Min, z1Max] = getHeightRangeForIntersection(
      polygon1,
      intersectionCoordinates
    );
    const [z2Min, z2Max] = getHeightRangeForIntersection(
      polygon2,
      intersectionCoordinates
    );

    // Adjust height ranges with buffer
    const z1BufferedMin = z1Min - buffer;
    const z1BufferedMax = z1Max + buffer;
    const z2BufferedMin = z2Min - buffer;
    const z2BufferedMax = z2Max + buffer;

    // Check if height ranges collide or overlap
    const heightCollides =
      z1BufferedMin < z2BufferedMax && z2BufferedMin < z1BufferedMax;
    const type: "overlap" | "collision" = heightCollides ? "collision" : "overlap";

    return {
      type,
      coordinates: intersectionCoordinates,
    };
  });

  return { intersections };
}

/**
 * Gets the height range (Z and M) of a polygon.
 *
 * @param polygon - The polygon as an array of coordinates [x, y, z, m].
 * @returns A tuple [zMin, zMax] representing the height range of the polygon.
 */
export function getHeightRange(polygon: Coordinate[]): [number, number] {
  let zMin = Infinity;
  let zMax = -Infinity;

  for (const [, , z = 0, m = 0] of polygon) {
    zMin = Math.min(zMin, z);
    zMax = Math.max(zMax, m);
  }

  return [zMin, zMax];
}

function getHeightRangeForIntersection(
  polygon: Coordinate[],
  intersection: number[][]
): [number, number] {
  let zMin = Infinity;
  let zMax = -Infinity;

  // Iterate through the intersection points
  for (const [ix, iy] of intersection) {
    // Find the corresponding point in the original polygon
    const matchingPoint = polygon.find(
      ([px, py]) => Math.abs(px - ix) < 1e-6 && Math.abs(py - iy) < 1e-6
    );
    if (matchingPoint) {
      const [, , z = 0, m = 0] = matchingPoint;
      zMin = Math.min(zMin, z);
      zMax = Math.max(zMax, m);
    }
  }

  return [zMin, zMax];
}

/**
 * Interpolates a polygon by adding intermediate points between existing vertices.
 *
 * @param polygon - The polygon as an array of coordinates [x, y, z, m].
 * @param steps - The number of interpolated points to add between each pair of vertices.
 * @returns The interpolated polygon as an array of coordinates [x, y, z, m].
 */
export function interpolatePolygon(
  polygon: Coordinate[],
  steps: number
): Coordinate[] {
  if (steps < 1 || polygon.length < 2) {
    return polygon; // No interpolation needed
  }

  const interpolated: Coordinate[] = [];

  for (let i = 0; i < polygon.length - 1; i++) {
    const start = polygon[i];
    const end = polygon[i + 1];

    // Add the start point
    interpolated.push(start);

    // Interpolate points between start and end
    for (let step = 1; step <= steps; step++) {
      const t = step / (steps + 1); // Interpolation factor

      const interpolatedPoint: Coordinate = [
        interpolateValue(start[0], end[0], t), // Interpolate X
        interpolateValue(start[1], end[1], t), // Interpolate Y
        interpolateValue(start[2] ?? 0, end[2] ?? 0, t), // Interpolate Z (default to 0 if undefined)
        interpolateValue(start[3] ?? 0, end[3] ?? 0, t), // Interpolate M (default to 0 if undefined)
      ];

      interpolated.push(interpolatedPoint);
    }
  }

  // Add the last point to close the polygon if needed
  interpolated.push(polygon[polygon.length - 1]);

  return interpolated;
}

/**
 * Linearly interpolates between two values.
 *
 * @param start - The start value.
 * @param end - The end value.
 * @param t - The interpolation factor (0 <= t <= 1).
 * @returns The interpolated value.
 */
function interpolateValue(start: number, end: number, t: number): number {
  return start + t * (end - start);
}

/**
 * Normalizes a vector to a length of 1. This function was generated with AI.
 * @param {Point} vector - The vector to normalize.
 * @returns {Point} The normalized vector.
 */
function normalizeVector(vector) {
  const magnitude = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
  if (magnitude === 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: vector.x / magnitude,
    y: vector.y / magnitude,
  };
}

/**
 * Extends the size of a polygon by a specified number of pixels. This function
 * was generated with AI.
 * @param {Polygon} polygon - The polygon to extend.
 * @param {number} pixels - The number of pixels to extend the polygon by.
 * @returns {Polygon} A new polygon with extended size.
 * // TODO This function doesn't seem to be too accurate, the buffered polygon
 * // is slightly wacky, we should convert to using the Turf library. I started
 * // doing that but didn't have much success and didn't want to get bogged
 * // down on the optimisation.
 */
export const addPolygonBuffer = memorize(
  function (params) {
    var { polygon, buffer, centroid } = params;

    polygon = polygon.map(convertPointTo3sf);

    // Sort the vertices of both polygons to ensure consistent winding order
    polygon = sortPolygonVerticesClockwise(polygon);

    if (polygon.length < 3) {
      throw new Error("A polygon must have at least 3 vertices.");
    }

    const newPolygon = [];
    for (const vertex of polygon) {
      // Calculate the vector from the centroid to the current vertex
      const vectorFromCentroid = {
        x: vertex.x - centroid.x,
        y: vertex.y - centroid.y,
      };

      // Normalize this vector to a length of 1
      const normalizedVector = normalizeVector(vectorFromCentroid);

      // Extend the polygon by moving each vertex along its normalized vector
      const newVertex = {
        x: vertex.x + normalizedVector.x * buffer,
        y: vertex.y + normalizedVector.y * buffer,
      };
      newPolygon.push(newVertex);
    }

    return newPolygon;
  },
  {
    cacheKey: (params) => "addPolygonBuffer:" + JSON.stringify(params),
  }
);

/**
 * Represents a 2D point or vector.
 * @typedef {{x: number, y: number}} Point
 */

/**
 * Represents a polygon as an array of vertices.
 * @typedef {Point[]} Polygon
 */

/**
 * Sorts a polygon's vertices in a clockwise order. This is crucial
 * for the Separating Axis Theorem to work correctly with non-ordered vertex lists.
 * This function was generated with AI.
 * @param {Polygon} polygon - The polygon to sort.
 * @returns {Polygon} A new array of vertices sorted clockwise.
 */
export function sortPolygonVerticesClockwise(polygon) {
  // Find the centroid of the polygon
  const center = { x: 0, y: 0 };
  for (const p of polygon) {
    center.x += p.x;
    center.y += p.y;
  }
  center.x /= polygon.length;
  center.y /= polygon.length;

  // Sort the vertices by the angle they make with the centroid
  return [...polygon].sort((a, b) => {
    const angleA = Math.atan2(a.y - center.y, a.x - center.x);
    const angleB = Math.atan2(b.y - center.y, b.x - center.x);
    return angleA - angleB;
  });
}

/**
 * Check if a polygon overlaps another polygon. This is the core of the parking
 * algorithm. We use the Turf library, however if speed over correctness
 * is a priority we could likely use a simpler check.
 */
export const checkOverlap = memorize(
  function checkOverlap(polygonA, polygonB) {
    polygonA = polygonA.map(convertPointTo3sf);
    polygonB = polygonB.map(convertPointTo3sf);

    polygonA = polygonA.map((o) => [o.x, o.y]);
    polygonB = polygonB.map((o) => [o.x, o.y]);

    var temp1 = [...polygonA, polygonA[0]];
    var temp2 = [...polygonB, polygonB[0]];
    //console.log('temp1', temp1)
    var polygonAGeoJson = polygon([temp1]);
    var polygonBGeoJson = polygon([temp2]);
    if (booleanOverlap(polygonAGeoJson, polygonBGeoJson)) return true;
    if (booleanContains(polygonAGeoJson, polygonBGeoJson)) return true;
    if (booleanContains(polygonBGeoJson, polygonAGeoJson)) return true;
    return false;
  },
  {
    cacheKey: (polygonA, polygonB) =>
      "checkOverlap:" +
      JSON.stringify(polygonA) +
      ":" +
      JSON.stringify(polygonB),
  }
);

/**
 * Represents a 2D point or vector.
 * @typedef {{x: number, y: number}} Point
 */
/**
 * Represents a polygon as an array of vertices.
 * @typedef {Point[]} Polygon
 */
/**
 * Represents a Bounding Box.
 * @typedef {{minX: number, maxX: number, minY: number, maxY: number}} BoundingBox
 */

const EPSILON = 1e-9;

// ---------------------------------------------------------------------
// --- GEOMETRY HELPER FUNCTIONS ---
// ---------------------------------------------------------------------

/**
 * Calculates the Axis-Aligned Bounding Box (AABB) for a polygon.
 */
export const getBoundingBox = memorize(
  function (polygon) {
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    for (const { x, y } of polygon) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    return { minX, maxX, minY, maxY };
  },
  {
    cacheKey: (polygon) => "getBoundingBox:" + JSON.stringify(polygon),
  }
);

/**
 * Checks if two Axis-Aligned Bounding Boxes overlap.
 */
const checkAABBOverlap = function (boxA, boxB) {
  // Check for separation on the X-axis
  if (boxA.maxX < boxB.minX || boxB.maxX < boxA.minX) {
    return false;
  }
  // Check for separation on the Y-axis
  if (boxA.maxY < boxB.minY || boxB.maxY < boxA.minY) {
    return false;
  }
  // If no separation found, they overlap
  return true;
};

/**
 * Checks if a point is on a line segment (simplified for this check).
 */
function isPointOnSegment(p, a, b) {
  const crossProduct = (p.y - a.y) * (b.x - a.x) - (p.x - a.x) * (b.y - a.y);
  if (Math.abs(crossProduct) > EPSILON) return false;

  const dotProduct = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
  if (dotProduct < 0) return false;

  const squaredLength = (b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y);
  if (dotProduct > squaredLength + EPSILON) return false;

  return true;
}

/**
 * Checks if a point is inside a polygon using the robust Ray Casting Algorithm.
 * **(The robust version from the previous turn is kept for final containment check)**
 */
const isPointInPolygon = memorize(
  function (point, polygon) {
    let intersections = 0;
    const n = polygon.length;

    for (let i = 0; i < n; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % n];

      // 1. Edge Check
      if (isPointOnSegment(point, p1, p2)) return true;

      const x = point.x;
      const y = point.y;

      const y1 = p1.y;
      const y2 = p2.y;
      const x1 = p1.x;
      const x2 = p2.x;

      // 2. Robust Ray Casting Logic
      if (
        ((y1 <= y && y < y2) || (y2 <= y && y < y1)) &&
        x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1
      ) {
        intersections++;
      }
    }
    return intersections % 2 !== 0;
  },
  {
    cacheKey: (polygon) => "isPointInPolygon:" + JSON.stringify(polygon),
  }
);

// ---------------------------------------------------------------------
// --- MAIN ROBUST OVERLAP FUNCTION ---
// ---------------------------------------------------------------------

/**
 * Checks if two polygons overlap using a fast AABB pre-check followed by a
 * robust Point-in-Polygon containment test.
 * * This method is highly optimized for performance and handles the complex
 * floating-point edge cases found in your test data.
 * This function was generated with AI.
 * @param {Polygon} polygonA - The first polygon.
 * @param {Polygon} polygonB - The second polygon.
 * @returns {boolean} True if the polygons overlap, false otherwise.
 */
export const checkOverlapQuick = memorize(
  function (polygonA, polygonB) {
    polygonA = polygonA.map(convertPointTo3sf);
    polygonB = polygonB.map(convertPointTo3sf);

    const boxA = getBoundingBox(polygonA);
    const boxB = getBoundingBox(polygonB);

    // 1. FAST PRE-CHECK: If Bounding Boxes don't overlap, Polygons can't overlap.
    if (!checkAABBOverlap(boxA, boxB)) {
      return false;
    }

    // 2. DETAILED CHECK: Use robust PIP only if boxes overlap.

    // Check if any vertex of A is inside B
    for (const vertex of polygonA) {
      if (isPointInPolygon(vertex, polygonB)) {
        return true;
      }
    }

    // Check if any vertex of B is inside A
    for (const vertex of polygonB) {
      if (isPointInPolygon(vertex, polygonA)) {
        return true;
      }
    }

    // After AABB overlap and no vertex containment found,
    // the complex nature of the polygon requires an edge-intersection
    // check (like SAT/decomposition). Since we are avoiding complex libraries,
    // for practical, fast collision, we assume no overlap at this point.
    return false;
  },
  {
    cacheKey: (polygonA, polygonB) =>
      "checkOverlapQuick:" +
      JSON.stringify(polygonA) +
      ":" +
      JSON.stringify(polygonB),
  }
);

export const getPolygonIntersection = function (polygonA, polygonB) {
  polygonA = polygonA.map((o) => [o.x, o.y]);
  polygonB = polygonB.map((o) => [o.x, o.y]);

  var temp1 = [...polygonA, polygonA[0]];
  var temp2 = [...polygonB, polygonB[0]];
  //console.log('temp1', temp1)
  var polygonAGeoJson = polygon([temp1]);
  var polygonBGeoJson = polygon([temp2]);

  return intersect(featureCollection([polygonAGeoJson, polygonBGeoJson]));
};

export const getPolygonOverlapAreaSize = function (polygonA, polygonB) {
  var intersection = getPolygonIntersection(polygonA, polygonB);
  if (!intersection) return 0;

  return area(intersection);
};
